import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarUI } from './ui/calendar';
import { Calendar as CalendarIcon, CheckCircle, CalendarDays, Loader2 } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { useCalendarHolidays } from '../hooks/useHolidays';

const Sv2ApprovalDialog = ({ open, onOpenChange, project, onApprovalComplete }) => {
    const { holidayDates, isHoliday, onMonthChange: onHolidayMonthChange } = useCalendarHolidays();
    const [approvalType, setApprovalType] = useState('approve'); // 'approve' or 'reschedule'
    const [rescheduleDate, setRescheduleDate] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [slotsAvailability, setSlotsAvailability] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [calendarMonth, setCalendarMonth] = useState(new Date());
    const [monthAvailability, setMonthAvailability] = useState({ availableDates: [], unavailableDates: [] });
    const [loadingMonthAvailability, setLoadingMonthAvailability] = useState(false);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [sv2Booking, setSv2Booking] = useState(null);
    const [loadingBooking, setLoadingBooking] = useState(false);

    // Reset state when dialog opens
    useEffect(() => {
        if (open && project) {
            setApprovalType('approve');
            setRescheduleDate(null);
            setSelectedSlot(null);
            setSlotsAvailability([]);
            setMonthAvailability({ availableDates: [], unavailableDates: [] });
            fetchSv2Booking();
        }
    }, [open, project]);

    // Fetch proposed SV2 booking details
    const fetchSv2Booking = async () => {
        if (!project?.id) return;
        setLoadingBooking(true);
        try {
            const response = await api.get(`/api/projects/${project.id}/site-visits`);
            setSv2Booking(response?.sv2 || null);
        } catch (error) {
            console.error('Error fetching SV2 booking:', error);
            toast.error('Failed to load SV2 booking details');
        } finally {
            setLoadingBooking(false);
        }
    };

    // Fetch month availability for calendar
    useEffect(() => {
        if (approvalType === 'reschedule' && calendarMonth) {
            fetchMonthAvailability(calendarMonth);
        }
    }, [calendarMonth, approvalType]);

    // Fetch slots when date changes
    useEffect(() => {
        if (approvalType === 'reschedule' && rescheduleDate) {
            fetchSlotAvailability(rescheduleDate);
        }
    }, [rescheduleDate, approvalType]);

    const fetchMonthAvailability = async (month) => {
        setLoadingMonthAvailability(true);
        try {
            const year = month.getFullYear();
            const monthNum = month.getMonth() + 1;
            const data = await api.getMonthAvailability(year, monthNum);
            const available = [];
            const unavailable = [];
            data.dates?.forEach(dateInfo => {
                const d = new Date(dateInfo.date);
                if (dateInfo.hasAvailability) available.push(d);
                else unavailable.push(d);
            });
            setMonthAvailability({ availableDates: available, unavailableDates: unavailable });
        } catch (error) {
            console.error('Error fetching month availability:', error);
        } finally {
            setLoadingMonthAvailability(false);
        }
    };

    const fetchSlotAvailability = async (date) => {
        setLoadingSlots(true);
        setSlotsAvailability([]);
        setSelectedSlot(null);
        try {
            const dateStr = format(date, 'yyyy-MM-dd');
            const data = await api.getSlotAvailability(dateStr);
            setSlotsAvailability(data.slots || []);
        } catch (error) {
            console.error('Error fetching slots:', error);
        } finally {
            setLoadingSlots(false);
        }
    };

    const handleSubmit = async () => {
        if (approvalType === 'reschedule' && (!rescheduleDate || !selectedSlot)) {
            toast.error('Please select a date and time slot for rescheduling');
            return;
        }

        setIsSubmitting(true);
        try {
            const params = {
                reschedule: approvalType === 'reschedule',
                newDate: approvalType === 'reschedule' ? format(rescheduleDate, 'yyyy-MM-dd') : null,
                newSlotId: approvalType === 'reschedule' ? selectedSlot.slotId : null,
            };

            await api.approveSv2(project.id, params);

            toast.success('SV2 approved successfully! Site visit has been scheduled.');
            onOpenChange(false);
            if (onApprovalComplete) onApprovalComplete();
        } catch (error) {
            console.error('Error approving SV2:', error);
            toast.error(error.response?.data?.message || error.message || 'Failed to approve SV2');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderSlotGrid = () => (
        loadingSlots ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading slots...
            </div>
        ) : slotsAvailability.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                {slotsAvailability.map((slot) => (
                    <Button
                        key={slot.slotId}
                        variant="outline"
                        className={`flex-col h-auto py-3 items-center ${selectedSlot?.slotId === slot.slotId
                            ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                            : slot.isAvailable
                                ? 'border-green-400 hover:bg-green-50'
                                : 'border-red-300 bg-red-50 opacity-60 cursor-not-allowed'
                            }`}
                        onClick={() => slot.isAvailable && setSelectedSlot(slot)}
                        disabled={!slot.isAvailable}
                    >
                        <span className="font-semibold text-sm text-center">{slot.slotName?.split('(')[0]?.trim()}</span>
                        <Badge className={`mt-1 text-xs ${slot.isAvailable ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-red-500 text-white'}`}>
                            {slot.isAvailable ? 'Available' : 'Fully Booked'}
                        </Badge>
                    </Button>
                ))}
            </div>
        ) : (
            <p className="text-sm text-gray-500 mt-2">No slots available for this date</p>
        )
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[95vw] sm:max-w-xl max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Approve Site Visit 2</DialogTitle>
                    <DialogDescription>
                        Review and approve the proposed SV2 schedule for {project?.clientName}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-2">
                    {/* Proposed booking info */}
                    {loadingBooking ? (
                        <div className="flex items-center justify-center py-4">
                            <Loader2 className="h-5 w-5 animate-spin text-blue-600 mr-2" />
                            <span>Loading SV2 details...</span>
                        </div>
                    ) : sv2Booking ? (
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <h3 className="font-semibold text-sm text-blue-800 mb-2">Proposed Schedule</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                <div>
                                    <Label className="text-gray-600">Date</Label>
                                    <p className="font-medium">
                                        {sv2Booking.scheduledDate
                                            ? format(new Date(sv2Booking.scheduledDate + 'T00:00:00'), 'MMM dd, yyyy')
                                            : 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <Label className="text-gray-600">Time Slot</Label>
                                    <p className="font-medium">{sv2Booking.slotName || 'N/A'}</p>
                                </div>
                                <div>
                                    <Label className="text-gray-600">SuperAligner</Label>
                                    <p className="font-medium">{sv2Booking.supervisorName || 'N/A'}</p>
                                </div>
                                <div>
                                    <Label className="text-gray-600">Status</Label>
                                    <Badge className="bg-amber-100 text-amber-700 border-amber-300 capitalize">
                                        {sv2Booking.status || 'proposed'}
                                    </Badge>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p className="text-red-600 text-sm">No SV2 booking found for this project.</p>
                    )}

                    {/* Approval options */}
                    <div className="space-y-3">
                        <Label className="text-sm font-semibold">Choose an option:</Label>

                        <Button
                            variant={approvalType === 'approve' ? 'default' : 'outline'}
                            className={`w-full justify-start ${approvalType === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                            onClick={() => setApprovalType('approve')}
                        >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve as proposed
                        </Button>

                        <Button
                            variant={approvalType === 'reschedule' ? 'default' : 'outline'}
                            className={`w-full justify-start ${approvalType === 'reschedule' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                            onClick={() => setApprovalType('reschedule')}
                        >
                            <CalendarDays className="w-4 h-4 mr-2" />
                            Approve with reschedule
                        </Button>
                    </div>

                    {/* Reschedule form */}
                    {approvalType === 'reschedule' && (
                        <div className="border rounded-lg p-4 space-y-4 bg-blue-50">
                            <h3 className="font-semibold text-sm">Reschedule Site Visit 2</h3>
                            <p className="text-xs text-gray-600">A new supervisor will be automatically assigned based on availability.</p>

                            <div>
                                <Label>Select Date (Green = Available, Red = Fully Booked)</Label>
                                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" className="w-full justify-start text-left font-normal mt-1">
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {rescheduleDate ? format(rescheduleDate, 'PPP') : 'Select a date'}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <CalendarUI
                                            mode="single"
                                            selected={rescheduleDate}
                                            onSelect={(date) => {
                                                setRescheduleDate(date);
                                                setCalendarOpen(false);
                                            }}
                                            disabled={(date) => date < new Date() || isHoliday(date)}
                                            month={calendarMonth}
                                            onMonthChange={(month) => { setCalendarMonth(month); onHolidayMonthChange(month); }}
                                            modifiers={{
                                                available: monthAvailability.availableDates,
                                                unavailable: monthAvailability.unavailableDates,
                                                holiday: holidayDates
                                            }}
                                            modifiersClassNames={{
                                                available: 'bg-green-100 text-green-900 hover:bg-green-200',
                                                unavailable: 'bg-red-100 text-red-900 hover:bg-red-200',
                                                holiday: 'holiday-day'
                                            }}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>

                            {rescheduleDate && (
                                <div>
                                    <Label>Select Time Slot</Label>
                                    {renderSlotGrid()}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Submit button */}
                    <Button
                        className="w-full bg-green-600 hover:bg-green-700"
                        onClick={handleSubmit}
                        disabled={
                            isSubmitting ||
                            !sv2Booking ||
                            (approvalType === 'reschedule' && (!rescheduleDate || !selectedSlot))
                        }
                    >
                        {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {approvalType === 'approve' ? 'Approve SV2' : 'Approve & Reschedule SV2'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default Sv2ApprovalDialog;
